/**
 * Reservedelsøgning: tekstsøgning + filtre (leverandør, type, tags, lager).
 */
import { CartAddEvent } from '@theme/events';

const MAX_TAG_FILTERS = 48;
const SORT_LOCALE = 'da';

function normalize(str) {
  if (str == null || str === '') return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function tokenize(query) {
  return normalize(query).split(/\s+/).filter(Boolean);
}

function buildBlob(product) {
  const variants = product.variants || [];
  const parts = [
    ...variants.flatMap((v) => [v.sku, v.barcode]),
    product.displaySku,
    product.displayBarcode,
    product.title,
    ...productTags(product),
    product.description,
  ];
  return normalize(parts.join(' '));
}

function allTokensMatch(product, tokens) {
  if (tokens.length === 0) return true;
  const blob = buildBlob(product);
  return tokens.every((t) => blob.includes(t));
}

function scoreProduct(product, tokens, rawQuery) {
  const q = normalize(rawQuery.trim());
  let score = 0;
  const variants = product.variants || [];
  const skus = variants.map((v) => normalize(v.sku));
  const barcodes = variants.map((v) => normalize(v.barcode));
  const title = normalize(product.title);
  const desc = normalize(product.description || '');
  const tagStr = normalize(productTags(product).join(' '));

  if (q.length > 0) {
    for (const sku of skus) {
      if (sku && sku === q) score += 1000;
    }
    for (const bc of barcodes) {
      if (bc && bc === q) score += 950;
    }
  }

  for (const t of tokens) {
    let tier = 0;
    for (const sku of skus) {
      if (!sku) continue;
      if (sku === t) tier = Math.max(tier, 100);
      else if (sku.includes(t)) tier = Math.max(tier, 50);
    }
    for (const bc of barcodes) {
      if (!bc) continue;
      if (bc === t) tier = Math.max(tier, 90);
      else if (bc.includes(t)) tier = Math.max(tier, 45);
    }
    if (title.includes(t)) tier = Math.max(tier, 30);
    if (tagStr.includes(t)) tier = Math.max(tier, 20);
    if (desc.includes(t)) tier = Math.max(tier, 8);
    score += tier;
  }
  return score;
}

function sortProducts(products, tokens, query) {
  const q = query.trim();
  return [...products].sort((a, b) => scoreProduct(b, tokens, q) - scoreProduct(a, tokens, q));
}

/**
 * @param {object} p
 * @param {{ vendor: string, productType: string, inStockOnly: boolean, selectedTags: string[] }} f
 */
function productTags(p) {
  return Array.isArray(p.tags) ? p.tags : [];
}

function passesFilters(p, f) {
  if (f.inStockOnly && p.available !== true) return false;
  if (f.vendor && String(p.vendor || '') !== f.vendor) return false;
  if (f.productType && String(p.productType || '') !== f.productType) return false;
  const tags = productTags(p);
  for (const t of f.selectedTags) {
    if (!tags.includes(t)) return false;
  }
  return true;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s) {
  return escapeHtml(s).replace(/'/g, '&#39;');
}

function getCartAddUrl() {
  return typeof Theme !== 'undefined' && Theme.routes?.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js';
}

/** Formatér beløb i øre (Shopify cents) til "1.234,56 kr". */
function formatMoneyOre(ore) {
  return (ore / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
}

function rangeLabel(min, max) {
  return min === max ? formatMoneyOre(min) : formatMoneyOre(min) + ' – ' + formatMoneyOre(max);
}

/**
 * Split priceMin/priceMax (øre, raw Shopify price) into ex-VAT + incl-VAT
 * labels. `taxesIncluded` mirrors Liquid's `cart.taxes_included` — it tells us
 * whether the raw price IS the incl-VAT amount (true) or the ex-VAT amount
 * (false), so we divide or multiply accordingly instead of assuming.
 */
function vatSplitLabels(priceMin, priceMax, rate, taxesIncluded) {
  const f = 1 + (rate || 0) / 100;
  if (taxesIncluded) {
    const exMin = Math.round(priceMin / f);
    const exMax = Math.round(priceMax / f);
    return { exLabel: rangeLabel(exMin, exMax), inclLabel: rangeLabel(priceMin, priceMax) };
  }
  const inclMin = Math.round(priceMin * f);
  const inclMax = Math.round(priceMax * f);
  return { exLabel: rangeLabel(priceMin, priceMax), inclLabel: rangeLabel(inclMin, inclMax) };
}

/**
 * ITG-2458: kort i "Populære reservedele". Kaldes kun med varer der har billede og pris > 0.
 * @param {object} product
 * @param {object} labels
 */
function renderPopularCard(product, labels) {
  const title = escapeHtml(product.title || '');
  const url = escapeAttr(product.url);
  const sku = product.displaySku
    ? `<span class="spare-parts-search__popular-sku">${escapeHtml(labels.sku || 'SKU')} ${escapeHtml(product.displaySku)}</span>`
    : '';
  const { exLabel, inclLabel } = vatSplitLabels(
    Number(product.priceMin) || 0,
    Number(product.priceMax) || 0,
    labels.vatRate,
    labels.taxesIncluded
  );
  const action =
    product.variantId && product.available !== false
      ? `<button type="button" class="button spare-parts-search__cart-btn" data-spare-parts-add data-variant-id="${escapeAttr(String(product.variantId))}">${escapeHtml(labels.add)}</button>`
      : `<span class="spare-parts-search__sold-out">${escapeHtml(labels.soldOut)}</span>`;
  return `<li class="spare-parts-search__popular-card">
    <a class="spare-parts-search__popular-media" href="${url}" tabindex="-1" aria-hidden="true">
      <img src="${escapeAttr(product.image)}" alt="" width="240" height="240" loading="lazy">
    </a>
    <a class="spare-parts-search__popular-title" href="${url}">${title}</a>
    ${sku}
    <span class="spare-parts-search__price">
      <span class="spare-parts-search__price-ex">${escapeHtml(exLabel)} <span class="spare-parts-search__price-vat">${escapeHtml(labels.exVat)}</span></span>
      <span class="spare-parts-search__price-incl">${escapeHtml(inclLabel)} ${escapeHtml(labels.inclVat)}</span>
    </span>
    ${action}
  </li>`;
}

/**
 * @param {object} product
 * @param {{ sku: string, ean: string, add: string, soldOut: string, placeholderLogo: string, placeholderLogoAlt: string, placeholderText: string }} labels
 */
function renderRow(product, labels) {
  const skuLabel = labels.sku || 'SKU';
  const eanLabel = labels.ean || 'EAN';
  const sku = product.displaySku || '';
  const ean = product.displayBarcode || '';
  const skuLine = sku
    ? `<span class="spare-parts-search__sku-line">${escapeHtml(skuLabel)} ${escapeHtml(sku)}</span>`
    : '';
  const eanLine = ean
    ? `<span class="spare-parts-search__ean-line">${escapeHtml(eanLabel)}: ${escapeHtml(ean)}</span>`
    : '';
  const meta = [skuLine, eanLine].filter(Boolean).join('<br>') || '—';

  const imgAlt = escapeAttr(product.title || '');
  const placeholderLogo = labels.placeholderLogo
    ? `<img class="spare-parts-search__thumb-placeholder-logo" src="${escapeAttr(labels.placeholderLogo)}" alt="${escapeAttr(labels.placeholderLogoAlt || '')}" width="96" height="30" loading="lazy">`
    : '';
  const placeholderText = escapeHtml(labels.placeholderText || 'Billede kommer snart');
  const thumb = product.image
    ? `<a href="${escapeAttr(product.url)}" aria-label="${imgAlt}"><img class="spare-parts-search__thumb" src="${escapeAttr(product.image)}" alt="${imgAlt}" width="80" height="80" loading="lazy"></a>`
    : `<a href="${escapeAttr(product.url)}" aria-label="${imgAlt}">
        <span class="spare-parts-search__thumb spare-parts-search__thumb--placeholder" aria-hidden="true">
          ${placeholderLogo}
          <span class="spare-parts-search__thumb-placeholder-text">${placeholderText}</span>
        </span>
      </a>`;

  const variantId = product.variantId;
  const available = product.available !== false;
  const priceMin = Number(product.priceMin) || 0;
  const priceMax = Number(product.priceMax) || 0;
  // Pris 0 = "på forespørgsel" — må aldrig kunne lægges i kurv (samme regel som resten af temaet).
  const isInquiry = priceMin <= 0;

  let priceCell;
  if (isInquiry) {
    priceCell = `<td class="spare-parts-search__cell-price spare-parts-search__price">
      <span class="spare-parts-search__price-inquiry">${escapeHtml(labels.inquiryPrice)}</span>
    </td>`;
  } else {
    const { exLabel, inclLabel } = vatSplitLabels(priceMin, priceMax, labels.vatRate, labels.taxesIncluded);
    priceCell = `<td class="spare-parts-search__cell-price spare-parts-search__price">
      <span class="spare-parts-search__price-ex">${escapeHtml(exLabel)} <span class="spare-parts-search__price-vat">${escapeHtml(labels.exVat)}</span></span>
      <span class="spare-parts-search__price-incl">${escapeHtml(inclLabel)} ${escapeHtml(labels.inclVat)}</span>
    </td>`;
  }

  let cartCell = '';
  if (isInquiry) {
    cartCell = `<td class="spare-parts-search__cell-cart">
      <button type="button" class="button spare-parts-search__cart-btn spare-parts-search__inquiry-btn" data-spare-parts-inquiry data-url="${escapeAttr(product.url)}">${escapeHtml(labels.inquiry)}</button>
    </td>`;
  } else if (variantId) {
    if (available) {
      cartCell = `<td class="spare-parts-search__cell-cart">
        <button type="button" class="button spare-parts-search__cart-btn" data-spare-parts-add data-variant-id="${escapeAttr(String(variantId))}">
          ${escapeHtml(labels.add)}
        </button>
      </td>`;
    } else {
      cartCell = `<td class="spare-parts-search__cell-cart">
        <span class="spare-parts-search__sold-out">${escapeHtml(labels.soldOut)}</span>
      </td>`;
    }
  } else {
    cartCell = `<td class="spare-parts-search__cell-cart"><span class="spare-parts-search__sold-out">—</span></td>`;
  }

  return `<tr class="spare-parts-search__row">
    <td class="spare-parts-search__cell-image">${thumb}</td>
    <td class="spare-parts-search__cell-name">
      <a class="spare-parts-search__link" href="${escapeAttr(product.url)}">${escapeHtml(product.title)}</a>
    </td>
    <td class="spare-parts-search__cell-sku">
      <div class="spare-parts-search__meta">${meta}</div>
    </td>
    ${priceCell}
    ${cartCell}
  </tr>`;
}

function readNumberFromCssVar(root, prop, fallback) {
  const raw = getComputedStyle(root).getPropertyValue(prop).trim();
  const n = parseInt(raw.replace(/ms/gi, '').trim(), 10);
  return Number.isFinite(n) ? n : fallback;
}

async function addVariantToCart(variantId, button) {
  button.disabled = true;
  try {
    const res = await fetch(getCartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      }),
    });
    const data = await res.json();
    if (data.status) {
      document.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { message: data.message, description: data.description },
        })
      );
      return;
    }
    document.dispatchEvent(
      new CartAddEvent(data, 'spare-parts-search', {
        source: 'spare-parts-search',
        variantId: String(variantId),
        sections: data.sections,
      })
    );
  } catch (e) {
    console.warn('Spare parts: add to cart failed', e);
  } finally {
    button.disabled = false;
  }
}

function initSection(root) {
  const sectionId = root.dataset.sectionId;
  if (!sectionId) return;

  const script = document.getElementById(`SparePartsData--${sectionId}`);
  if (!script?.textContent) return;

  const input = root.querySelector('[data-spare-parts-input]');
  const tbody = root.querySelector('[data-spare-parts-tbody]');
  const emptyEl = root.querySelector('[data-spare-parts-empty]');
  const idleEl = root.querySelector('[data-spare-parts-idle]');
  const loadingEl = root.querySelector('[data-spare-parts-loading]');
  const live = root.querySelector('[data-spare-parts-live]');
  const clearBtn = root.querySelector('[data-spare-parts-clear]');
  const table = root.querySelector('[data-spare-parts-table]');
  const filtersEl = root.querySelector('[data-spare-parts-filters]');
  const vendorSel = root.querySelector('[data-spare-parts-filter-vendor]');
  const typeSel = root.querySelector('[data-spare-parts-filter-type]');
  const stockCb = root.querySelector('[data-spare-parts-filter-stock]');
  const tagsContainer = root.querySelector('[data-spare-parts-tags-container]');
  const resetBtn = root.querySelector('[data-spare-parts-filters-reset]');
  const popularEl = root.querySelector('[data-spare-parts-popular]');
  const popularGrid = root.querySelector('[data-spare-parts-popular-grid]');

  if (!input || !tbody || !table) return;

  const debounceMs = readNumberFromCssVar(root, '--spare-parts-debounce', 200);
  const maxResultsRaw = readNumberFromCssVar(root, '--spare-parts-max-results', 100);
  const maxResults = Math.min(500, Math.max(1, Number.isFinite(maxResultsRaw) ? maxResultsRaw : 100));
  const strFilterAll = root.dataset.strFilterAll || 'All';

  let initial;
  try {
    initial = JSON.parse(script.textContent);
  } catch (e) {
    console.warn('Spare parts: invalid JSON', e);
    return;
  }

  /** URL'erne på de sider der mangler, klar til at hentes på én gang. */
  function restPageUrls(chunk) {
    const total = Number(chunk && chunk.totalPages) || 1;
    const current = Number(chunk && chunk.page) || 1;
    const base = chunk && chunk.collectionUrl;
    if (!base || total <= current) return [];
    const urls = [];
    for (let p = current + 1; p <= total; p++) {
      urls.push(base + '?view=spare-parts-data&page=' + p);
    }
    return urls;
  }

  let products = [...(initial.products || [])];
  /**
   * ITG-1345: resten af siderne hentes som ren JSON via ?view=spare-parts-data og
   * parallelt. Før blev hele HTML-dokumenter hentet i kæde — 748 KB og ~1,2 s pr.
   * side, 9 sider for de 2005 reservedele, plus DOMParser på hver.
   * @type {string[]}
   */
  let pendingPageUrls = restPageUrls(initial);
  let isFetchingRest = false;
  /** Antal produkter filter-UI sidst er bygget for */
  let filterCatalogSize = 0;

  const strResults = root.dataset.strStatusResults || 'Showing __COUNT__ results.';
  const strNone = root.dataset.strStatusNone || 'No results.';
  const strLoadingCatalog = root.dataset.strLoadingCatalog || 'Loading…';
  const labels = {
    sku: root.dataset.labelSku || 'SKU',
    ean: root.dataset.labelEan || 'EAN',
    add: root.dataset.strAdd || 'Add to cart',
    soldOut: root.dataset.strSoldOut || 'Sold out',
    placeholderLogo: root.dataset.placeholderLogo || '',
    placeholderLogoAlt: root.dataset.placeholderLogoAlt || '',
    placeholderText: root.dataset.placeholderText || 'Billede kommer snart',
    exVat: root.dataset.strExVat || 'ekskl. moms',
    inclVat: root.dataset.strInclVat || 'inkl. moms',
    inquiry: root.dataset.strInquiry || 'Send forespørgsel',
    inquiryPrice: root.dataset.strInquiryPrice || 'Pris på forespørgsel',
    vatRate: parseFloat(root.dataset.vatRate) || 0,
    taxesIncluded: root.dataset.taxesIncluded === 'true',
  };

  // ITG-2458: første side er samlingens 250 bedst sælgende (standardsortering).
  let hasPopular = false;
  if (popularEl instanceof HTMLElement && popularGrid) {
    const count = Math.max(1, Number(popularEl.dataset.count) || 8);
    const inStockOnly = popularEl.dataset.inStockOnly !== 'false';
    const picks = products
      .filter((p) => p.image && Number(p.priceMin) > 0 && (!inStockOnly || p.available !== false))
      .slice(0, count);
    popularGrid.innerHTML = picks.map((p) => renderPopularCard(p, labels)).join('');
    hasPopular = picks.length > 0;
    popularGrid.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-spare-parts-add]');
      if (!(btn instanceof HTMLButtonElement) || !btn.dataset.variantId) return;
      e.preventDefault();
      addVariantToCart(btn.dataset.variantId, btn);
    });
  }

  function readFilters() {
    const selectedTags = [];
    if (tagsContainer) {
      tagsContainer.querySelectorAll('[data-spare-parts-tag-cb]:checked').forEach((cb) => {
        if (cb instanceof HTMLInputElement && cb.value) selectedTags.push(cb.value);
      });
    }
    return {
      vendor: vendorSel instanceof HTMLSelectElement ? vendorSel.value : '',
      productType: typeSel instanceof HTMLSelectElement ? typeSel.value : '',
      inStockOnly: stockCb instanceof HTMLInputElement ? stockCb.checked : false,
      selectedTags,
    };
  }

  let suppressFilterChange = false;

  function rebuildFilterControls(preserved) {
    const vPres = preserved.vendor;
    const tPres = preserved.productType;
    const tagsPres = new Set(preserved.selectedTags);
    const stockPres = preserved.inStockOnly;

    if (vendorSel instanceof HTMLSelectElement) {
      const vendors = [...new Set(products.map((p) => p.vendor).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, SORT_LOCALE)
      );
      vendorSel.innerHTML =
        `<option value="">${escapeHtml(strFilterAll)}</option>` +
        vendors.map((v) => `<option value="${escapeAttr(v)}">${escapeHtml(v)}</option>`).join('');
      if (vPres && vendors.includes(vPres)) vendorSel.value = vPres;
    }

    if (typeSel instanceof HTMLSelectElement) {
      const types = [...new Set(products.map((p) => p.productType).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, SORT_LOCALE)
      );
      typeSel.innerHTML =
        `<option value="">${escapeHtml(strFilterAll)}</option>` +
        types.map((t) => `<option value="${escapeAttr(t)}">${escapeHtml(t)}</option>`).join('');
      if (tPres && types.includes(tPres)) typeSel.value = tPres;
    }

    if (tagsContainer) {
      const tagCounts = new Map();
      products.forEach((p) => {
        productTags(p).forEach((tag) => {
          if (!tag) return;
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        });
      });
      const topTags = [...tagCounts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], SORT_LOCALE))
        .slice(0, MAX_TAG_FILTERS)
        .map((x) => x[0]);

      tagsContainer.innerHTML = topTags
        .map((tag) => {
          const checked = tagsPres.has(tag) ? ' checked' : '';
          return `<label class="spare-parts-search__tag-chip"><input type="checkbox" data-spare-parts-tag-cb value="${escapeAttr(tag)}"${checked} /><span>${escapeHtml(tag)}</span></label>`;
        })
        .join('');
    }

    if (stockCb instanceof HTMLInputElement) stockCb.checked = stockPres;
  }

  function ensureFilterControlsMatchCatalog() {
    if (products.length === filterCatalogSize) return;
    const preserved = readFilters();
    suppressFilterChange = true;
    try {
      rebuildFilterControls(preserved);
      filterCatalogSize = products.length;
    } finally {
      suppressFilterChange = false;
    }
  }

  tbody.addEventListener('click', (e) => {
    const inquiryBtn = e.target.closest('[data-spare-parts-inquiry]');
    if (inquiryBtn instanceof HTMLElement && inquiryBtn.dataset.url) {
      e.preventDefault();
      window.location.assign(inquiryBtn.dataset.url);
      return;
    }
    const btn = e.target.closest('[data-spare-parts-add]');
    if (!btn || !(btn instanceof HTMLButtonElement)) return;
    const vid = btn.dataset.variantId;
    if (!vid) return;
    e.preventDefault();
    addVariantToCart(vid, btn);
  });

  function setEmptyState(filteredEmpty, noProducts) {
    if (noProducts) {
      emptyEl.textContent = emptyEl.dataset.msgCollection || '';
      emptyEl.hidden = false;
      table.hidden = true;
      return;
    }
    if (filteredEmpty) {
      emptyEl.textContent = emptyEl.dataset.msgFiltered || '';
      emptyEl.hidden = false;
      table.hidden = true;
      return;
    }
    emptyEl.hidden = true;
    table.hidden = false;
  }

  function render(query) {
    const q = query.trim();

    if (!q) {
      tbody.innerHTML = '';
      table.hidden = true;
      emptyEl.hidden = true;
      if (loadingEl) loadingEl.hidden = true;
      if (filtersEl) filtersEl.hidden = true;
      if (popularEl) popularEl.hidden = !hasPopular;
      if (idleEl) {
        idleEl.hidden = false;
        if (idleEl.id) input.setAttribute('aria-describedby', idleEl.id);
      }
      if (live) live.textContent = '';
      return;
    }

    if (idleEl) {
      idleEl.hidden = true;
      if (idleEl.id) input.removeAttribute('aria-describedby');
    }
    if (popularEl) popularEl.hidden = true;
    if (filtersEl) filtersEl.hidden = false;

    if (pendingPageUrls.length) {
      if (!isFetchingRest) {
        isFetchingRest = true;
        if (loadingEl) {
          loadingEl.textContent = strLoadingCatalog;
          loadingEl.hidden = false;
        }
        table.hidden = true;
        emptyEl.hidden = true;
        tbody.innerHTML = '';
        if (filtersEl) filtersEl.hidden = true;

        (async () => {
          const urls = pendingPageUrls;
          try {
            // Alle resterende sider på én gang. En enkelt side der fejler koster
            // kun sine egne produkter — resten af kataloget er stadig søgbart.
            const chunks = await Promise.all(
              urls.map((url) =>
                fetch(url, { credentials: 'same-origin' })
                  .then((res) => (res.ok ? res.json() : null))
                  .catch(() => null),
              ),
            );
            const manglende = chunks.filter((c) => !c).length;
            if (manglende) {
              console.warn(`Spare parts: ${manglende} af ${urls.length} sider kunne ikke hentes`);
            }
            for (const chunk of chunks) {
              if (chunk && Array.isArray(chunk.products)) products = products.concat(chunk.products);
            }
          } catch (e) {
            console.warn('Spare parts: pagination fetch failed', e);
          } finally {
            pendingPageUrls = [];
            isFetchingRest = false;
            if (loadingEl) loadingEl.hidden = true;
            render(input.value);
          }
        })();
      } else {
        if (loadingEl) loadingEl.hidden = false;
        table.hidden = true;
        emptyEl.hidden = true;
        if (filtersEl) filtersEl.hidden = true;
        if (live) live.textContent = strLoadingCatalog;
      }
      return;
    }

    ensureFilterControlsMatchCatalog();
    const f = readFilters();

    if (resetBtn) {
      const hasFilters =
        Boolean(f.vendor) || Boolean(f.productType) || f.inStockOnly || f.selectedTags.length > 0;
      resetBtn.hidden = !hasFilters;
    }

    const tokens = tokenize(query);
    let working = products.filter((p) => allTokensMatch(p, tokens));
    working = working.filter((p) => passesFilters(p, f));
    const sorted = sortProducts(working, tokens, query);
    const slice = sorted.slice(0, maxResults);

    tbody.innerHTML = slice.map((p) => renderRow(p, labels)).join('');

    const noProducts = products.length === 0;
    const filteredEmpty = !noProducts && slice.length === 0;
    setEmptyState(filteredEmpty, noProducts);

    if (live) {
      if (noProducts) {
        live.textContent = '';
      } else if (filteredEmpty) {
        live.textContent = strNone;
      } else {
        live.textContent = strResults.replace('__COUNT__', String(slice.length));
      }
    }
  }

  let debounceTimer;
  input.addEventListener('input', () => {
    if (clearBtn) clearBtn.hidden = input.value.length === 0;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => render(input.value), debounceMs);
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      input.value = '';
      clearBtn.hidden = true;
      render('');
      input.focus();
    });
  }

  if (filtersEl) {
    filtersEl.addEventListener('change', () => {
      if (suppressFilterChange) return;
      render(input.value);
    });
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      if (vendorSel instanceof HTMLSelectElement) vendorSel.value = '';
      if (typeSel instanceof HTMLSelectElement) typeSel.value = '';
      if (stockCb instanceof HTMLInputElement) stockCb.checked = false;
      if (tagsContainer) {
        tagsContainer.querySelectorAll('[data-spare-parts-tag-cb]').forEach((cb) => {
          if (cb instanceof HTMLInputElement) cb.checked = false;
        });
      }
      render(input.value);
    });
  }

  const presetQ = new URLSearchParams(window.location.search).get('spare_parts_q');
  const initialQuery = presetQ != null ? presetQ.trim() : '';
  if (initialQuery) {
    input.value = initialQuery;
    if (clearBtn) clearBtn.hidden = false;
    render(initialQuery);
  } else {
    render('');
  }
}

function init() {
  document.querySelectorAll('.spare-parts-search[data-section-id]').forEach((el) => initSection(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
